import { get } from 'lodash-es';
import { HotspotType } from '@marvelapp/core';
import { areCommentsVisible } from './comment';
import { isInHandoffMode } from './handoff';
import { isShowingOverlay } from './overlay';
import { getCurrentScreen, getSectionID, isTransitionComplete } from './screen';
export function getCurrentOverlayHotspot(state) {
    if (get(state.project.config, 'hotspotMap')) {
        return state.project.config.hotspotMap[state.screen.currentOverlayHotspot.id];
    }
    return null;
}
export function getHotspotByID(state, id) {
    return get(state, ['project', 'config', 'hotspotMap', id]);
}
export function getHeaderHotspots(state, screen) {
    return getVisibleHotspots(state, 'header.hotspots', screen);
}
export function getFooterHotspots(state, screen) {
    return getVisibleHotspots(state, 'footer.hotspots', screen);
}
export function getBodyHotspots(state, screen, hostScreen) {
    return getVisibleHotspots(state, 'hotspots', screen, hostScreen);
}
export function getFixedHotspots(state, screen) {
    return getVisibleHotspots(state, 'fixedHotspots', screen);
}
export function doesHotspotExist(state, hotspotID) {
    return getHotspotByID(state, hotspotID);
}
export function areHotspotsVisible(state) {
    return state.hotspots.areVisible;
}
export function areHotspotsLoaded(state) {
    return state.hotspots.areLoaded;
}
// There's some complex rules around when we render hotspots depending on
// whether it's the incoming or outgoing screen, whether the screen transition
// is complete and whether we're in comment or handoff mode and what type of
// hotspots there are -- unfortunately embeds and smart containers are mixed in
// with regular hotspots. Someday we might want to split these out ...
//
// (In hindisght I wonder if it might have been simpler to use CSS rather than
// conditional rendering)
function getVisibleHotspots(state, screenProperty, screen, 
// We need hostScreen here for smart containers. Hotspots in a smart
// container belong to the smart container's destination screen but it's the
// host screen that we want to check to see if it's the current screen/overlay
// and whether we want to render the hotspots or not.
//
// +---------------------------------+
// | Screen id = 1                   |
// |                                 |
// |                                 |
// |  +----------------------+       |
// |  | Scroll container     |       |
// |  | screenId = 2         |       |
// |  |                      |       |
// |  | +---------+          |       |
// |  | | hotspot |          |       |
// |  | +---------+          |       |
// |  +----------------------+       |
// |                                 |
// +---------------------------------+
hostScreen) {
    var currentScreen = getCurrentScreen(state);
    var hotspots = get(screen, screenProperty, []);
    var screenToCheck = hostScreen || screen;
    if (areCommentsVisible(state) || isInHandoffMode(state)) {
        // If we're in comment or handoff mode we don't want to render regular
        // hotspots (so you can't flash them or interact with them). We do render
        // embeds and smart containers as they're part of the content of the
        // current screen (we're assuming we're dealing with the current screen
        // here. In fact, there's a bug whereby you can open comments or handoff
        // during a screen transition but not going to worry about that here as we
        // should probably fix that elsewhere)
        return hotspots.filter(function (h) { return h.type === HotspotType.Embed || h.smartContainer; });
    }
    if (!isTransitionComplete(state)) {
        // While we're animating between screens we don't render regular hotspots on
        // the incoming or outgoing screen so you can't click or flash them and
        // interrupt the transition.
        //
        // Additionally we don't render embeds. They prevent the
        // animation being smooth as they're initialising during the transition
        // (because we only keep at most 2 screens rendered at one -- possibly if we
        // rendered then all we wouldn't need to do this)
        //
        // We do render smart containers but might change that if they cause
        // animation problems like embeds.
        //
        // Note there's a bug here with overlays. isTransitionComplete only tells us
        // if a screen -> screen transition is in progress. As a consequence embeds
        // render while animating in an overlay which probably trashes the smooth
        // animation as it would for screens.
        return hotspots.filter(function (h) { return h.smartContainer; });
    }
    // Here we can assume we're checking the current screen or overlay as when
    // there's no transition the previous screen doesn't render at all.
    if (screenToCheck.id === currentScreen.id) {
        // Current screen
        if (isShowingOverlay(state)) {
            // When showing an overlay we don't render regular hotspots on the screen
            // underneath so they don't flash when flashing hotspots on the overlay
            return hotspots.filter(function (h) { return h.type === HotspotType.Embed || h.smartContainer; });
        }
        return hotspots;
    }
    // Current overlay so render all hotspots!
    return hotspots;
}
export function staysInSection(state, id) {
    var hotspot = getHotspotByID(state, id);
    var sectionID = getSectionID(state);
    if (!sectionID) {
        return true;
    }
    var screenMap = state.project.config.screenMap;
    var destinationScreen = screenMap[hotspot.destinationScreenID];
    return (destinationScreen.sectionId === sectionID ||
        hotspot.isOverlay ||
        hotspot.smartContainer
    // TODO allow hovers too?
    );
}
